import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import { handleResponseCode } from "../../../app/utilities/helpers";
import GetProxy from "../../../app/GetProxy";

const cookies = new Cookies();
const proxy = GetProxy();

const ASSET_TYPE_API_TO_UI = {
    SERVER: "Server",
    FIREWALL: "Firewall",
    NETWORK: "Network",
    OTHERS: "Others",
};

const NETWORK_API_TO_UI = {
    IT_ENTERPRISE: "IT Enterprise",
    TELCO: "Telco",
};

const STATUS_API_TO_UI = {
    PENDING_APPROVAL: "Pending",
    APPROVED: "Approved",
    REJECTED: "Rejected",
};

const transformApprovalItem = (item) => ({
    id: item.id,
    dccName: item.dccName || "",
    hostName: item.hostName || "",
    ipAddress: item.ip || "",
    assetType: ASSET_TYPE_API_TO_UI[item.assetType] || item.assetType || "",
    location: item.location || "",
    environment: item.environment || "",
    network: NETWORK_API_TO_UI[item.network] || item.network || "",
    scanType: item.scanType || "",
    assetMode: item.assetMode || "",
    status: STATUS_API_TO_UI[item.status] || item.status || "",
    requestedBy: item.requestedBy || item.createdBy || "",
    requestedOn: item.requestedOn || item.createdDt || "",
    updatedBy: item.updatedBy || "",
    updatedOn: item.updateDt || "",
    comment: item.rejectionReason || item.comment || "",
});

export const getApprovalList = createAsyncThunk(
    "post/getApprovalList",
    async ({ pageNumber, itemsPerPage, payload }) => {
        const apiToken = cookies.get("apitoken");
        const username = cookies.get("username");
        const response = await fetch(
            `${proxy}/sassp/asset/pendingApprovalList/${pageNumber}/${itemsPerPage}`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "DTOP-API-TOKEN": apiToken,
                    USERNAME: username,
                },
                body: JSON.stringify(payload),
            }
        );
        handleResponseCode(response);
        const data = await response.json();
        return data;
    }
);

export const approveOrRejectAsset = createAsyncThunk(
    "post/approveOrRejectAsset",
    async (payload) => {
        const apiToken = cookies.get("apitoken");
        const username = cookies.get("username");
        const response = await fetch(`${proxy}/sassp/asset/approveOrReject`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "DTOP-API-TOKEN": apiToken,
                USERNAME: username,
            },
            body: JSON.stringify(payload),
        });
        handleResponseCode(response);
        const data = await response.json();
        return data;
    }
);

const initialState = {
    approvalListData: {
        data: [],
        totalCount: 0,
    },
    approvalListLoading: false,
    updateStatusLoading: false,
    error: "",
    apiError: false,
};

export const AssetApprovalSlice = createSlice({
    name: "AssetApprovalSlice",
    initialState,
    reducers: {
        setErrorAlert(state) {
            state.apiError = false;
        },
        mockUpdateStatus(state, action) {
            const { id, status, remarks } = action.payload;
            const index = state.approvalListData.data.findIndex(
                (item) => item.id === id
            );
            if (index !== -1) {
                state.approvalListData.data[index].status = status;
                state.approvalListData.data[index].remarks = remarks || "";
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getApprovalList.pending, (state) => {
                state.approvalListLoading = true;
                state.apiError = false;
            })
            .addCase(getApprovalList.fulfilled, (state, action) => {
                state.approvalListLoading = false;
                const items = (action.payload?.data || []).map(transformApprovalItem);
                state.approvalListData = {
                    data: items,
                    totalCount: action.payload?.totalCount ?? items.length,
                };
            })
            .addCase(getApprovalList.rejected, (state, action) => {
                state.approvalListLoading = false;
                state.approvalListData = { data: [], totalCount: 0 };
                state.error = action.error?.message || "Failed to load approval list";
                state.apiError = true;
            })
            .addCase(approveOrRejectAsset.pending, (state) => {
                state.updateStatusLoading = true;
                state.apiError = false;
            })
            .addCase(approveOrRejectAsset.fulfilled, (state) => {
                state.updateStatusLoading = false;
            })
            .addCase(approveOrRejectAsset.rejected, (state, action) => {
                state.updateStatusLoading = false;
                state.error = action.error?.message || "Failed to update asset status";
                state.apiError = true;
            });
    },
});

export const { setErrorAlert, mockUpdateStatus } = AssetApprovalSlice.actions;
export default AssetApprovalSlice.reducer;
